import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography
} from '@mui/material';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { SIDEBAR_VALUE } from '../sidebarValues';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const STATUS_COLOR = {
  active: 'secondary',
  completed: 'success',
  draft: 'default',
  cancelled: 'warning'
};

const formatDate = (val) => (val ? new Date(val).toLocaleDateString() : '-');

export default function Pathways({ data, reportSummary }) {
  const t = useAutoTranslation();

  const navigate = useNavigate();
  const { setSnack } = React.useContext(SnackContext);

  // MongoDB _id of the selected node (only present for registered patients)
  const probandUserId = data?._id;

  const [instances, setInstances] = React.useState([]);
  const [templates, setTemplates] = React.useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState('');
  const [loadingInstances, setLoadingInstances] = React.useState(false);
  const [assigning, setAssigning] = React.useState(false);

  // TGCLOUD-1669 Phase 1 — the collapsed-row summary. `instances` lives only in
  // this component's state, so the row above cannot see it without this.
  // A node that is not a registered patient never fetches (no probandUserId),
  // which is a settled "nothing here" rather than a pending one.
  React.useEffect(() => {
    if (!reportSummary) return;
    if (probandUserId && loadingInstances) {
      reportSummary(SIDEBAR_VALUE.pathways, { status: 'pending' });
      return;
    }
    const active = instances.filter(
      (i) => i?.status && String(i.status).toLowerCase() === 'active'
    ).length;
    reportSummary(SIDEBAR_VALUE.pathways, {
      status: 'ready',
      count: instances.length,
      active,
      nextDue: null
    });
  }, [reportSummary, probandUserId, loadingInstances, instances]);

  const fetchInstances = React.useCallback(() => {
    if (!probandUserId) return;
    setLoadingInstances(true);
    axios
      .get(`${APICONSTANTS.pathwayInstances}?probandUserId=${probandUserId}`)
      .then(({ data: res }) => {
        setInstances(res?.instances || []);
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: t('Unable to load pathway data for this patient'),
          severity: 'error'
        });
      })
      .finally(() => setLoadingInstances(false));
  }, [probandUserId, setSnack]);

  // Fetch all live templates for the assign dropdown
  React.useEffect(() => {
    axios
      .get(APICONSTANTS.pathwaysTemplates)
      .then(({ data: res }) => {
        const all = res?.templates || res || [];
        setTemplates(all.filter((t) => t.status === 'live'));
      })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    fetchInstances();
    setSelectedTemplateId('');
  }, [fetchInstances]);

  const handleAssign = () => {
    if (!selectedTemplateId) return;
    if (!probandUserId) {
      setSnack({
        status: true,
        msg: t('This node is not linked to a registered patient.'),
        severity: 'warning'
      });
      return;
    }
    setAssigning(true);
    axios
      .post(APICONSTANTS.pathwayInstances, {
        pathwayTemplateId: selectedTemplateId,
        probandUserId
      })
      .then(() => {
        setSnack({
          status: true,
          msg: t('Pathway assigned successfully'),
          severity: 'success'
        });
        setSelectedTemplateId('');
        fetchInstances();
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: err?.response?.data?.message || t('Failed to assign pathway'),
          severity: 'error'
        });
      })
      .finally(() => setAssigning(false));
  };

  // IDs of templates already assigned (any status) so we can filter them out
  const assignedTemplateIds = new Set(
    instances.map((i) => String(i.pathwayTemplateId))
  );

  const availableTemplates = templates.filter(
    (t) => !assignedTemplateIds.has(String(t._id))
  );

  if (!probandUserId) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary'
        }}
      >
        {t('Select a registered patient node to view or assign pathways.')}
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {/* ── Assigned pathways ── */}
      <Box>
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={{
            fontWeight: 700
          }}
        >
          {t('Assigned Pathways')}
        </Typography>

        {loadingInstances ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 1
            }}
          >
            <CircularProgress size={20} />
          </Box>
        ) : instances.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary'
            }}
          >
            {t('This patient is not enrolled in any pathways.')}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {instances.map((instance) => {
              const template = instance.template;
              const currentStage = template?.stages?.find(
                (s) => s.stageKey === instance.currentStageKey
              );

              return (
                <Paper key={instance._id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <AltRouteIcon fontSize="small" color="action" />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600
                          }}
                        >
                          {template?.name || t('Unknown Pathway')}
                        </Typography>
                      </Box>
                      <Chip
                        label={instance.status}
                        size="small"
                        color={STATUS_COLOR[instance.status] || 'default'}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={
                          currentStage
                            ? t('Stage: {{name}}', { name: currentStage.name })
                            : instance.currentStageKey
                            ? t('Stage: {{currentStageKey}}', {
                                currentStageKey: instance.currentStageKey
                              })
                            : t('No stage')
                        }
                        size="small"
                        variant="outlined"
                      />
                      {currentStage?.type && (
                        <Chip
                          label={currentStage.type.replace(/_/g, ' ')}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary'
                      }}
                    >
                      {t('Assigned:')} {formatDate(instance.assignedAt)}
                      {instance.completedAt &&
                        ` · ${t('Completed: {{date}}', {
                          date: formatDate(instance.completedAt)
                        })}`}
                    </Typography>

                    <Divider />

                    {/* <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        history.push(
                          CONSTANTS.pathwayBoardPath.replace(
                            ':_id',
                            String(instance.pathwayTemplateId)
                          )
                        )
                      }
                    >
                      Open Board
                    </Button> */}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>
      <Divider />
      {/* ── Assign new pathway ── */}
      <Box>
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={{
            fontWeight: 700
          }}
        >
          {t('Assign Pathway')}
        </Typography>

        {templates.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary'
            }}
          >
            {t('No live pathway templates available.')}
          </Typography>
        ) : availableTemplates.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary'
            }}
          >
            {t('Patient is already enrolled in all available pathways.')}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel id="pathway-assign-label">
                {t('Select Pathway')}
              </InputLabel>
              <Select
                labelId="pathway-assign-label"
                value={selectedTemplateId}
                label={t('Select Pathway')}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
              >
                {availableTemplates.map((t) => (
                  <MenuItem key={t._id} value={t._id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              size="small"
              variant="contained"
              startIcon={
                assigning ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <AddIcon />
                )
              }
              disabled={!selectedTemplateId || assigning}
              onClick={handleAssign}
            >
              {assigning ? t('Assigning...') : t('Assign')}
            </Button>
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
